import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

export type LegalDocumentType = 'terms' | 'privacy';
type SupportedLanguage = 'en' | 'ar';
type Direction = 'ltr' | 'rtl';

interface LegalDocumentMeta {
  title: string;
  lastUpdated: string;
}

interface LegalDocumentConfig {
  byLang: Record<SupportedLanguage, {
    path: string;
    meta: LegalDocumentMeta;
    direction: Direction;
  }>;
}

export interface ResolvedLegalDocument {
  path: string;
  meta: LegalDocumentMeta;
  direction: Direction;
}

@Injectable({
  providedIn: 'root'
})
export class LegalDocumentContentService {
  private readonly _configs: Record<LegalDocumentType, LegalDocumentConfig> = {
    terms: {
      byLang: {
        en: {
          path: 'assets/legal/terms.en.html',
          meta: {
            title: 'Terms & Conditions',
            lastUpdated: 'Last updated: 1 June 2025'
          },
          direction: 'ltr'
        },
        ar: {
          path: 'assets/legal/terms.ar.html',
          meta: {
            title: 'الشروط والأحكام',
            lastUpdated: 'آخر تحديث: 1 يونيو 2023'
          },
          direction: 'rtl'
        }
      }
    },
    privacy: {
      byLang: {
        en: {
          path: 'assets/legal/privacy.en.html',
          meta: {
            title: 'Privacy Policy',
            lastUpdated: 'Last updated: 16 Sept 2025'
          },
          direction: 'ltr'
        },
        ar: {
          path: 'assets/legal/privacy.ar.html',
          meta: {
            title: 'سياسة الخصوصية',
            lastUpdated: 'آخر تحديث: 16 سبتمبر 2022'
          },
          direction: 'rtl'
        }
      }
    }
  };

  constructor(private readonly _http: HttpClient) { }

  resolveDocument(
    documentType: LegalDocumentType,
    currentLang?: string,
  ): ResolvedLegalDocument {
    const language: SupportedLanguage = currentLang === 'ar' ? 'ar' : 'en';
    const config = this._configs[documentType].byLang[language];
    return {
      path: config.path,
      meta: config.meta,
      direction: config.direction,
    };
  }

  loadDocumentHtml(path: string): Observable<string> {
    return this._http.get(path, { responseType: 'text' });
  }
}
